import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Plus } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useToast } from '../components/ui/use-toast';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { useAuth } from '../hooks/useAuth';
import { isSuperAdmin } from '../types/user';
import { climbScansService, ScanValidationError } from '../services/climb-scans';
import { zonesService } from '../services/zones';
import { gymsService } from '../services/gyms';
import { ScanDefaultsForm } from '../components/gym-scans/ScanDefaultsForm';
import { Textarea } from '../components/ui/textarea';
import { ColorRow, type ColorFormState } from '../components/gym-scans/ColorRow';
import type { ClimbScan, ClimbScanColor, SaveColorDraftData } from '../types/climb-scan';
import { boundingBoxOf } from '../types/climb-scan';
import { isRectangle, type OutlinePoint } from '../components/climbs/hold-outline';
import { BOX_GRID } from '../gym-admin/components/scans/detected-holds';
import type { Zone } from '../types/zone';

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function StatusBadge({ status }: { status: ClimbScan['status'] }) {
  const { t } = useTranslation();
  const colors: Record<ClimbScan['status'], string> = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    ready: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status]}`}>
      {t(`climbScan.status.${status}`)}
    </span>
  );
}

export default function GymScansPage() {
  const { gymSlug, scanId } = useParams<{ gymSlug: string; scanId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  // Starting a scan by hand costs a model call and wakes a GPU, so it is a
  // platform admin's to spend. A gym's own staff photograph the wall and
  // ScanPendingClimbScansJob looks at it — they are told that it is coming, not
  // offered a button. Mirrors `authorize_scan!`, which is what enforces it.
  const { user } = useAuth();
  const canScan = isSuperAdmin(user);

  const [scan, setScan] = useState<ClimbScan | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [expandedColorId, setExpandedColorId] = useState<string | null>(null);
  const [formStates, setFormStates] = useState<Record<string, ColorFormState>>({});
  const [savingColorId, setSavingColorId] = useState<string | null>(null);
  const [deletingColorId, setDeletingColorId] = useState<string | null>(null);
  const [zones, setZones] = useState<Zone[]>([]);
  const [addingColor, setAddingColor] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [colorErrors, setColorErrors] = useState<Record<string, Record<string, string>>>({});
  const [gymSuggestions, setGymSuggestions] = useState<string[]>([]);
  const [editingSuggestions, setEditingSuggestions] = useState('');
  const [savingSuggestions, setSavingSuggestions] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Colors that already received an auto-suggested name once; never auto-fill
  // them again so the user can clear the field and type a custom name.
  const prefilledColorIdsRef = useRef<Set<string>>(new Set());

  const fetchScan = useCallback(async () => {
    if (!gymSlug || !scanId) return;
    try {
      const data = await climbScansService.getScan(gymSlug, scanId);
      setScan(data);
      return data;
    } catch {
      toast({ title: t('climbScan.loadError'), variant: 'destructive' });
    }
  }, [gymSlug, scanId, t, toast]);

  useEffect(() => {
    setLoading(true);
    fetchScan().finally(() => setLoading(false));
    if (gymSlug) {
      gymsService.getGymBySlug(gymSlug).then(gym => {
        const suggestions = (gym.climb_name_suggestions || '')
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean);
        setGymSuggestions(suggestions);
        setEditingSuggestions(gym.climb_name_suggestions || '');
      }).catch(() => {});
    }
  }, [fetchScan, gymSlug]);

  useEffect(() => {
    if (!scan?.location?.slug) return;
    zonesService.getZonesForLocation(scan.location.slug, 'gym')
      .then(setZones)
      .catch(() => {});
  }, [scan?.location?.slug]);

  // Pre-fill colors without a name with a random unused suggestion, but only
  // once per color: after the first auto-fill the field is the user's to edit
  // (including clearing it to type a custom name).
  useEffect(() => {
    if (!scan || gymSuggestions.length === 0) return;

    const usedNames = new Set<string>();
    for (const color of scan.colors) {
      const name = (formStates[color.id]?.climbName ?? color.climb?.name) || '';
      if (name && gymSuggestions.includes(name)) usedNames.add(name);
    }

    const available = shuffleArray(gymSuggestions.filter(s => !usedNames.has(s)));
    let idx = 0;

    const updates: Record<string, ColorFormState> = {};
    for (const color of scan.colors) {
      if (color.status === 'completed') continue;
      if (prefilledColorIdsRef.current.has(color.id)) continue;
      const currentName = (formStates[color.id]?.climbName ?? color.climb?.name ?? color.climb_name) || '';
      if (currentName) {
        prefilledColorIdsRef.current.add(color.id);
        continue;
      }
      if (idx >= available.length) break;
      updates[color.id] = {
        climbName: available[idx],
        gradeId: formStates[color.id]?.gradeId ?? color.climb?.grade_id ?? '',
        colorName: formStates[color.id]?.colorName ?? color.color_name ?? '',
      };
      prefilledColorIdsRef.current.add(color.id);
      idx++;
    }

    if (Object.keys(updates).length > 0) {
      setFormStates(prev => ({ ...prev, ...updates }));
    }
  }, [scan, gymSuggestions, formStates]);

  useEffect(() => {
    if (!scan) return;
    if (scan.status === 'pending' || scan.status === 'processing') {
      pollingRef.current = setInterval(async () => {
        const updated = await fetchScan();
        if (updated && updated.status !== 'pending' && updated.status !== 'processing') {
          if (pollingRef.current) clearInterval(pollingRef.current);
        }
      }, 5000);
    } else {
      if (pollingRef.current) clearInterval(pollingRef.current);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [scan?.status, fetchScan]);

  useEffect(() => {
    if (scan?.status === 'failed' && scan.error_message) {
      console.warn('Scan processing error:', scan.error_message);
    }
  }, [scan?.status, scan?.error_message]);

  const drawAnnotations = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = img.offsetWidth;
    canvas.height = img.offsetHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!expandedColorId || !scan) return;

    const color = scan.colors.find(c => c.id === expandedColorId);
    if (!color?.detected_holds.length) return;

    const naturalW = img.naturalWidth;
    const naturalH = img.naturalHeight;
    if (!naturalW || !naturalH) return;

    const elemW = img.offsetWidth;
    const elemH = img.offsetHeight;
    const scale = Math.min(elemW / naturalW, elemH / naturalH);
    const scaledW = naturalW * scale;
    const scaledH = naturalH * scale;
    const offsetX = (elemW - scaledW) / 2;
    const offsetY = (elemH - scaledH) / 2;

    const hexColor = color.hex_colors[0] ?? '#ffffff';
    ctx.strokeStyle = hexColor;
    ctx.lineWidth = 2.5;
    ctx.shadowColor = 'rgba(0,0,0,0.8)';
    ctx.shadowBlur = 5;

    // A hold that knows its own shape is traced; one that is only a rectangle
    // says nothing about shape and keeps the ring it has always been drawn as.
    for (const hold of color.detected_holds) {
      const outline = hold.outline as OutlinePoint[];
      ctx.beginPath();

      if (isRectangle(outline)) {
        const box = boundingBoxOf(outline);
        const cx = offsetX + ((box.x + box.width / 2) / BOX_GRID) * scaledW;
        const cy = offsetY + ((box.y + box.height / 2) / BOX_GRID) * scaledH;
        const rx = Math.max((box.width / (BOX_GRID * 2)) * scaledW, 8);
        const ry = Math.max((box.height / (BOX_GRID * 2)) * scaledH, 8);
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      } else {
        outline.forEach(([x, y], index) => {
          const px = offsetX + (x / BOX_GRID) * scaledW;
          const py = offsetY + (y / BOX_GRID) * scaledH;
          if (index === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        });
        ctx.closePath();
      }

      ctx.stroke();
    }
  }, [expandedColorId, scan]);

  useEffect(() => {
    drawAnnotations();
  }, [drawAnnotations]);

  useBreadcrumb(
    () => ({
      onBack: () => navigate(`/gyms/${gymSlug}/scans`),
      segments: gymSlug
        ? [
            { label: scan?.location?.name ?? gymSlug, onClick: () => navigate(`/gyms/${gymSlug}`) },
            { label: t('climbScan.scans'), onClick: () => navigate(`/gyms/${gymSlug}/scans`) },
            { label: new Date(scan?.created_at ?? Date.now()).toLocaleDateString() },
          ]
        : [],
      loading,
    }),
    [gymSlug, scan?.location?.name, scan?.created_at, loading, t]
  );

  const remainingSuggestions = gymSuggestions
  const showSuggestionsForm = remainingSuggestions.length < 5;

  async function handleSaveSuggestions() {
    if (!gymSlug) return;
    setSavingSuggestions(true);
    try {
      const newSuggestions = editingSuggestions.split('\n').map(s => s.trim()).filter(Boolean);
      await gymsService.updateGym(gymSlug, {
        climb_name_suggestions: newSuggestions.join('\n') || undefined,
      });
      setGymSuggestions(newSuggestions);
    } catch {
      toast({ title: t('climbScan.saveSuggestionsError'), variant: 'destructive' });
    } finally {
      setSavingSuggestions(false);
    }
  }

  function getFormState(colorId: string, color: ClimbScanColor): ColorFormState {
    return formStates[colorId] ?? {
      climbName: color.climb?.name ?? color.climb_name ?? '',
      gradeId: color.climb?.grade_id ?? '',
      colorName: color.color_name ?? '',
    };
  }

  function updateFormState(colorId: string, patch: Partial<ColorFormState>) {
    if (colorErrors[colorId]) {
      setColorErrors(prev => { const next = { ...prev }; delete next[colorId]; return next; });
    }
    setFormStates(prev => ({
      ...prev,
      [colorId]: { ...getFormState(colorId, scan!.colors.find(c => c.id === colorId)!), ...patch },
    }));
  }

  // Pick a random suggestion not used by another color (and different from the
  // current one) for the given color.
  function drawSuggestion(colorId: string) {
    if (!scan) return;
    const usedNames = new Set<string>();
    for (const color of scan.colors) {
      if (color.id === colorId) continue;
      const name = (formStates[color.id]?.climbName ?? color.climb?.name) || '';
      if (name) usedNames.add(name);
    }
    const current = formStates[colorId]?.climbName ?? '';
    const available = gymSuggestions.filter(s => !usedNames.has(s) && s !== current);
    if (available.length === 0) return;
    const pick = available[Math.floor(Math.random() * available.length)];
    prefilledColorIdsRef.current.add(colorId);
    updateFormState(colorId, { climbName: pick });
  }

  function getGrades() {
    if (!scan?.location) return [];
    return (scan.climb_type === 'boulder'
      ? scan.location.boulder_grading_system?.grades
      : scan.location.route_grading_system?.grades) ?? [];
  }

  async function handleSaveDraft(color: ClimbScanColor, andNavigate: boolean) {
    if (!gymSlug || !scanId) return;
    const form = getFormState(color.id, color);
    const savedName = form.climbName.trim();
    const usedSuggestion = savedName && gymSuggestions.includes(savedName) ? savedName : null;
    setSavingColorId(color.id);
    const data: SaveColorDraftData = {
      name: savedName || null,
      grade_id: form.gradeId || null,
      color_name: form.colorName || null,
    };
    try {
      const updatedColor = await climbScansService.saveDraftColor(gymSlug, scanId, color.id, data);
      setScan(prev => prev ? {
        ...prev,
        colors: prev.colors.map(c => c.id === color.id ? updatedColor : c),
      } : prev);
      setExpandedColorId(null);
      if (usedSuggestion) {
        const newSuggestions = gymSuggestions.filter(s => s !== usedSuggestion);
        setGymSuggestions(newSuggestions);
        setEditingSuggestions(newSuggestions.join('\n'));
        gymsService.updateGym(gymSlug, {
          climb_name_suggestions: newSuggestions.join('\n') || undefined,
        }).catch(console.error);
      }
      if (andNavigate && updatedColor.climb) {
        const climbType = scan!.climb_type === 'boulder' ? 'boulders' : 'routes';
        navigate(`/gyms/${gymSlug}/${climbType}/${updatedColor.climb.slug}/edit?climb_scan_color_id=${color.id}`);
      } else {
        toast({ title: t('climbScan.draftSaved') });
      }
    } catch (error) {
      if (error instanceof ScanValidationError) {
        setColorErrors(prev => ({ ...prev, [color.id]: error.fieldErrors }));
      } else {
        toast({ title: t('climbScan.saveError'), variant: 'destructive' });
      }
    } finally {
      setSavingColorId(null);
    }
  }

  async function handleDeleteColor(colorId: string) {
    if (!gymSlug || !scanId) return;
    setDeletingColorId(colorId);
    try {
      await climbScansService.deleteColor(gymSlug, scanId, colorId);
      setScan(prev => prev ? {
        ...prev,
        colors: prev.colors.filter(c => c.id !== colorId),
      } : prev);
      if (expandedColorId === colorId) setExpandedColorId(null);
    } catch {
      toast({ title: t('climbScan.deleteError'), variant: 'destructive' });
    } finally {
      setDeletingColorId(null);
    }
  }

  async function handleAddColor() {
    if (!gymSlug || !scanId) return;
    setAddingColor(true);
    try {
      const newColor = await climbScansService.createColor(gymSlug, scanId);
      setScan(prev => prev ? { ...prev, colors: [newColor, ...prev.colors] } : prev);
      const usedNames = new Set(
        Object.values(formStates).map(f => f.climbName).filter(Boolean)
      );
      const pool = shuffleArray(remainingSuggestions.filter(s => !usedNames.has(s)));
      const nextSuggestion = pool[0] ?? '';
      prefilledColorIdsRef.current.add(newColor.id);
      setFormStates(prev => ({ ...prev, [newColor.id]: { climbName: nextSuggestion, gradeId: '', colorName: '' } }));
      setExpandedColorId(newColor.id);
    } catch {
      toast({ title: t('climbScan.addColorError'), variant: 'destructive' });
    } finally {
      setAddingColor(false);
    }
  }

  async function handleScan() {
    if (!gymSlug || !scanId) return;
    setRetrying(true);
    try {
      const updated = await climbScansService.startScan(gymSlug, scanId);
      setScan(updated);
    } catch {
      toast({ title: t('climbScan.retryError'), variant: 'destructive' });
    } finally {
      setRetrying(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card><CardContent className="p-6 text-center text-muted-foreground">{t('climbScan.loadError')}</CardContent></Card>
      </div>
    );
  }

  const grades = getGrades();
  const activeColors = scan.colors.filter(c => c.status !== 'completed');
  const completedColors = scan.colors.filter(c => c.status === 'completed');

  function renderColor(color: ClimbScanColor) {
    return (
      <ColorRow
        key={color.id}
        color={color}
        grades={grades}
        gymSlug={gymSlug!}
        scanClimbType={scan!.climb_type}
        isExpanded={expandedColorId === color.id}
        formState={getFormState(color.id, color)}
        fieldErrors={colorErrors[color.id] ?? {}}
        isSaving={savingColorId === color.id}
        isDeleting={deletingColorId === color.id}
        onToggleExpand={() => setExpandedColorId(expandedColorId === color.id ? null : color.id)}
        onFormChange={patch => updateFormState(color.id, patch)}
        onDrawSuggestion={gymSuggestions.length > 0 ? () => drawSuggestion(color.id) : undefined}
        onSaveDraft={andNavigate => handleSaveDraft(color, andNavigate)}
        onDelete={() => handleDeleteColor(color.id)}
        onNavigateToClimb={() => {
          const type = scan!.climb_type === 'boulder' ? 'boulders' : 'routes';
          navigate(`/gyms/${gymSlug}/${type}/${color.climb!.slug}`);
        }}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Scan header */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium capitalize">{scan.climb_type}</span>
              <StatusBadge status={scan.status} />
            </div>
            <span className="text-xs text-muted-foreground">
              {new Date(scan.created_at).toLocaleDateString()}
            </span>
          </div>

          {scan.image_url && (
            <>
              <div
                className="relative rounded-lg overflow-hidden max-h-80 cursor-pointer"
                onClick={() => setImageDialogOpen(true)}
              >
                <img
                  ref={imgRef}
                  src={scan.image_url}
                  alt={t('climbScan.scanImage')}
                  className="w-full object-contain max-h-80"
                  onLoad={drawAnnotations}
                />
                <canvas
                  ref={canvasRef}
                  className="absolute inset-0 w-full h-full pointer-events-none"
                />
              </div>
              <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
                <DialogContent className="max-w-screen-lg p-2">
                  <img
                    src={scan.image_url}
                    alt={t('climbScan.scanImage')}
                    className="w-full object-contain max-h-[90vh]"
                  />
                </DialogContent>
              </Dialog>
            </>
          )}

          {/* `pending` stopped meaning *queued* when the upload stopped
              starting anything: a waiting photo is one nobody has asked about
              yet, so it gets the button rather than the spinner. */}
          {scan.status === 'pending' && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">{t('climbScan.waitingMessage')}</p>
              {canScan && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleScan}
                    disabled={retrying}
                    className="gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} />
                    {t('climbScan.scanNow')}
                  </Button>
                  <p className="text-xs text-muted-foreground">{t('climbScan.firstScanHint')}</p>
                </>
              )}
            </div>
          )}

          {scan.status === 'processing' && (
            <p className="text-sm text-muted-foreground text-center animate-pulse">
              {t('climbScan.processingMessage')}
            </p>
          )}

          {scan.status === 'failed' && (
            <div className="space-y-2">
              <p className="text-sm text-destructive">{t('climbScan.failedMessage')}</p>
              {/* Same press, same price, so the same gate. The batch only takes
                  photos that are waiting, so a failed one does stop here —
                  which it already did before the button existed, and the job
                  has had its own three attempts by the time it says failed. */}
              {canScan && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleScan}
                  disabled={retrying}
                  className="gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} />
                  {t('climbScan.retry')}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scan settings — zone and route setters */}
      <ScanDefaultsForm
        scan={scan}
        gymSlug={gymSlug!}
        zones={zones}
        onScanUpdated={setScan}
      />

      {/* Name suggestions — shown when fewer than 5 names remain */}
      {showSuggestionsForm && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{t('climbScan.nameSuggestionsTitle')}</p>
              <span className="text-xs text-muted-foreground">
                {t('climbScan.nameSuggestionsRemaining', { count: remainingSuggestions.length })}
              </span>
            </div>
            <Textarea
              rows={4}
              value={editingSuggestions}
              onChange={(e) => setEditingSuggestions(e.target.value)}
              placeholder={t('gymSettings.climbNameSuggestionsPlaceholder')}
            />
            <Button size="sm" onClick={handleSaveSuggestions} disabled={savingSuggestions}>
              {savingSuggestions ? t('gymSettings.saving') : t('climbScan.saveSuggestions')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {scan.status === 'ready' && scan.colors.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            {t('climbScan.noColors')}
          </CardContent>
        </Card>
      )}

      {/* Add color button */}
      {scan.status === 'ready' && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={handleAddColor}
            disabled={addingColor}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('climbScan.addColor')}
          </Button>
        </div>
      )}

      {/* Active colors (pending + draft) */}
      {activeColors.map(renderColor)}

      {/* Completed colors inside a closed accordion */}
      {completedColors.length > 0 && (
        <Accordion type="single" collapsible className="space-y-2">
          <AccordionItem value="completed" className="border rounded-lg overflow-hidden">
            <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:no-underline">
              <span className="flex items-center gap-2">
                {t('climbScan.colorStatus.completed')}
                <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                  {completedColors.length}
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-0 pb-0">
              <div className="space-y-0 divide-y divide-border">
                {completedColors.map(renderColor)}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
}
